import React, { useEffect, useState } from "react";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { toast } from "sonner";
import { FileSignature, Plus, Loader2, X } from "lucide-react";
import ContractSection from "./ContractSection";

/**
 * Lista todos os contratos de um cliente (múltiplos contratos ativos suportados).
 *
 * - Fetch em `/clients/{id}/contracts` (endpoint plural).
 * - Cabeçalho com botão "+ Novo Contrato" que abre um modal com o formulário.
 * - Cada contrato renderiza um `ContractSection` completo (com todas as ações
 *   de pagamento/renovação/quitação/PDF/cancelar/transfer sócio/estornar/etc.).
 * - Contratos ativos aparecem primeiro (por venc. crescente), depois quitados/cancelados.
 * - Sinaliza `seq` (#001, #002…) por ordem de criação por cliente.
 */
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="fc-label">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}

export default function ContractsListSection({ clientId, canEdit, onContractChange, isIndicador }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Create form state
  const [cap, setCap] = useState("");
  const [pct, setPct] = useState("");
  const [vencDay, setVencDay] = useState("");
  const [firstDue, setFirstDue] = useState("");
  const [socioId, setSocioId] = useState("");
  const [capitalSource, setCapitalSource] = useState("socio");
  const [socios, setSocios] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/contracts`);
      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);
  useEffect(() => {
    if (!isIndicador && canEdit) {
      api.get("/socios").then(r => setSocios(r.data.filter(s => s.status === "ativo"))).catch(() => {});
    }
  }, [isIndicador, canEdit]);

  const resetForm = () => {
    setCap(""); setPct(""); setVencDay(""); setFirstDue(""); setSocioId(""); setCapitalSource("socio");
  };

  const create = async (e) => {
    e.preventDefault();
    const isEmpresa = capitalSource === "empresa";
    if (!isEmpresa && !socioId) {
      toast.error("Selecione o sócio responsável pelo capital");
      return;
    }
    setBusy(true);
    try {
      const body = {
        capital: Number(cap),
        interest_percent: Number(pct),
        capital_source: capitalSource,
      };
      if (!isEmpresa) body.socio_id = socioId;
      if (vencDay) body.vencimento_day = Number(vencDay);
      if (firstDue) body.first_due_date = firstDue;
      await api.post(`/clients/${clientId}/contracts`, body);
      toast.success(isEmpresa ? "Novo contrato criado (Capital Próprio)" : "Novo contrato criado");
      setCreateOpen(false);
      resetForm();
      load();
      onContractChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar contrato");
    } finally {
      setBusy(false);
    }
  };

  const handleContractChange = () => {
    load();
    onContractChange?.();
  };

  const active = contracts.filter(c => c.status === "ativo" || c.status === "atrasado" || c.status === "contratado");
  const closed = contracts.filter(c => !(c.status === "ativo" || c.status === "atrasado" || c.status === "contratado"));
  const activeNormal = active.filter(c => !c.in_recovery);
  const activeRecovery = active.filter(c => c.in_recovery);
  const totalActiveCapital = active.reduce((sum, c) => sum + (Number(c.capital) || 0), 0);

  if (loading) {
    return (
      <section className="fc-card p-6" data-testid="contracts-list-loading">
        <div className="h-24 shimmer rounded" />
      </section>
    );
  }

  return (
    <div className="space-y-5" data-testid="contracts-list-section">
      {/* Cabeçalho + Botão Novo Contrato + Resumo */}
      <section className="fc-card p-5 sm:p-6" data-testid="contracts-list-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <FileSignature size={16} className="text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-display text-[17px] font-semibold tracking-tight">
                Contratos <span className="text-[#737373] font-normal text-[13px] ml-1">({contracts.length})</span>
              </h2>
              {active.length > 0 && (
                <div className="text-[12px] text-[#A3A3A3] mt-0.5" data-testid="contracts-list-summary">
                  <span className="text-[#D4AF37] font-mono-tab">{formatBRL(totalActiveCapital)}</span>
                  {" "}em {active.length} contrato{active.length > 1 ? "s" : ""} ativo{active.length > 1 ? "s" : ""}
                  {activeRecovery.length > 0 && (
                    <span className="ml-2 text-amber-300/80">
                      · {activeRecovery.length} em Recuperação
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {canEdit && !isIndicador && (
            <button
              onClick={() => setCreateOpen(true)}
              className="fc-btn-gold flex items-center gap-2 text-sm"
              data-testid="btn-new-contract"
            >
              <Plus size={14}/> Novo Contrato
            </button>
          )}
        </div>

        {contracts.length === 0 && !createOpen && (
          <div className="mt-4 py-8 text-center text-[#737373] text-sm border border-dashed border-[#262626] rounded-md">
            Nenhum contrato ainda. Clique em &quot;Novo Contrato&quot; para iniciar a primeira operação.
          </div>
        )}

        {/* Resumo compacto — cabeçalhos das linhas (só quando há vários) */}
        {contracts.length > 0 && (
          <div className="mt-4 grid gap-1.5" data-testid="contracts-list-preview">
            {contracts.map(c => (
              <div
                key={c.id}
                className={`flex items-center justify-between text-[12.5px] px-3 py-2 rounded border ${
                  c.in_recovery
                    ? "border-amber-500/40 bg-amber-500/[0.04]"
                    : c.status === "ativo" || c.status === "atrasado" || c.status === "contratado"
                    ? "border-[#D4AF37]/30 bg-[#D4AF37]/5"
                    : "border-[#262626] bg-[#0f0f0f] text-[#737373]"
                }`}
                data-testid={`contract-preview-${c.seq}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono-tab text-[#D4AF37] font-medium">
                    #{String(c.seq || 0).padStart(3, "0")}
                  </span>
                  <span className={`badge-base text-[10px] ${
                    c.status === "ativo" || c.status === "contratado" ? "badge-aprovado" :
                    c.status === "atrasado" ? "badge-negado" :
                    c.status === "quitado" ? "badge-pago" :
                    "bg-neutral-800 text-neutral-400"
                  }`}>
                    {c.status === "ativo" ? "Ativo" :
                     c.status === "atrasado" ? "Atrasado" :
                     c.status === "contratado" ? "Ativo" :
                     c.status === "quitado" ? "Quitado" :
                     c.status === "convertido" ? "Convertido" :
                     "Cancelado"}
                  </span>
                  {c.in_recovery && (
                    <span className="badge-base text-[9.5px] bg-amber-500/15 text-amber-200 border-amber-500/40 tracking-wider" data-testid={`preview-recovery-badge-${c.seq}`}>
                      RECUPERAÇÃO
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[#A3A3A3]">
                  <span className="font-mono-tab">{formatBRL(c.capital)}</span>
                  {c.current_cycle?.due_date && (
                    <span className="hidden sm:inline">Venc. {formatDateBR(c.current_cycle.due_date)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal de criação */}
      {createOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-stretch sm:items-center justify-center sm:p-4 overflow-y-auto"
          onClick={() => { if (!busy) { setCreateOpen(false); resetForm(); } }}
          data-testid="new-contract-modal"
        >
          <div
            className="fc-card w-full sm:max-w-2xl sm:rounded-lg p-5 sm:p-7 m-0 sm:m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-[17px] font-semibold flex items-center gap-2">
                <Plus size={16} className="text-[#D4AF37]"/> Novo Contrato
              </h3>
              <button
                onClick={() => { if (!busy) { setCreateOpen(false); resetForm(); } }}
                className="text-[#737373] hover:text-white transition-colors"
                data-testid="new-contract-close"
              >
                <X size={20}/>
              </button>
            </div>
            <form onSubmit={create} className="grid sm:grid-cols-2 gap-4" data-testid="new-contract-form">
              <div className="sm:col-span-2">
                <label className="fc-label">Origem do capital</label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setCapitalSource("socio")}
                    className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                      capitalSource === "socio"
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E8C547]"
                        : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                    }`}
                    data-testid="new-contract-source-socio"
                  >
                    Sócio externo (split 50/50)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCapitalSource("empresa")}
                    className={`flex-1 px-3 py-2 rounded border text-sm transition ${
                      capitalSource === "empresa"
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                        : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                    }`}
                    data-testid="new-contract-source-empresa"
                  >
                    Capital Próprio (100% empresa)
                  </button>
                </div>
              </div>
              <Field label="Capital emprestado (R$)">
                <input required type="number" step="0.01" min="0.01" className="fc-input" value={cap} onChange={e=>setCap(e.target.value)} data-testid="new-contract-capital"/>
              </Field>
              <Field label="Percentual de juros (% ao mês)">
                <input required type="number" step="0.01" min="0" className="fc-input" value={pct} onChange={e=>setPct(e.target.value)} data-testid="new-contract-interest"/>
              </Field>
              {capitalSource === "socio" && (
                <Field label="Sócio responsável pelo capital">
                  <select required className="fc-input" value={socioId} onChange={e=>setSocioId(e.target.value)} data-testid="new-contract-socio">
                    <option value="">— Selecione —</option>
                    {socios.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.capital_disponivel !== undefined
                          ? `${s.name} (disponível: R$ ${(s.capital_disponivel ?? 0).toLocaleString("pt-BR", {minimumFractionDigits: 2})})`
                          : s.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Dia de vencimento (1-31)" hint="Mantido em todos os meses">
                <input type="number" min="1" max="31" className="fc-input" value={vencDay} onChange={e=>setVencDay(e.target.value)} placeholder="Ex: 15" data-testid="new-contract-venc-day"/>
              </Field>
              <Field label="Primeiro vencimento (opcional)" hint="Se vazio, calcula +1 mês a partir de hoje">
                <input type="date" className="fc-input" value={firstDue} onChange={e=>setFirstDue(e.target.value)} data-testid="new-contract-first-due"/>
              </Field>
              <div className="sm:col-span-2 flex gap-2 justify-end mt-2">
                <button type="button" onClick={() => { setCreateOpen(false); resetForm(); }} disabled={busy} className="fc-btn-ghost">Cancelar</button>
                <button type="submit" disabled={busy} className="fc-btn-gold flex items-center gap-2" data-testid="new-contract-submit">
                  {busy && <Loader2 size={14} className="animate-spin"/>} Criar contrato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contratos separados por Portfolio — iter51 */}
      {(() => {
        const normalCts = contracts.filter(c => !c.in_recovery);
        const recoveryCts = contracts.filter(c => c.in_recovery);
        return (
          <>
            {normalCts.length > 0 && (
              <div className="space-y-4" data-testid="portfolio-section-normal">
                {recoveryCts.length > 0 && (
                  <div className="flex items-center gap-2 pt-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"/>
                    <h3 className="font-display text-[13.5px] uppercase tracking-[0.14em] text-emerald-300/90">
                      Contratos ativos <span className="text-[#525252] font-normal ml-1">({normalCts.length})</span>
                    </h3>
                    <div className="flex-1 border-t border-[#1a1a1a] ml-2"/>
                  </div>
                )}
                {normalCts.map(c => (
                  <ContractSection
                    key={c.id}
                    clientId={clientId}
                    contractId={c.id}
                    seq={c.seq}
                    canEdit={canEdit}
                    isIndicador={isIndicador}
                    onContractChange={handleContractChange}
                    hideCreate={true}
                  />
                ))}
              </div>
            )}

            {recoveryCts.length > 0 && (
              <div className="space-y-4" data-testid="portfolio-section-recovery">
                <div className="flex items-center gap-2 pt-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400"/>
                  <h3 className="font-display text-[13.5px] uppercase tracking-[0.14em] text-amber-300/90">
                    Em Recuperação <span className="text-[#525252] font-normal ml-1">({recoveryCts.length})</span>
                  </h3>
                  <div className="flex-1 border-t border-[#1a1a1a] ml-2"/>
                </div>
                <div className="rounded-md border border-amber-500/20 bg-amber-500/[0.03] p-1 space-y-4">
                  {recoveryCts.map(c => (
                    <ContractSection
                      key={c.id}
                      clientId={clientId}
                      contractId={c.id}
                      seq={c.seq}
                      canEdit={canEdit}
                      isIndicador={isIndicador}
                      onContractChange={handleContractChange}
                      hideCreate={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
